import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  Box,
  Button,
  MenuItem,
} from '@material-ui/core'
import { Translate } from 'react-localize-redux'
import { Input, SelectedMenu } from 'components'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import { PLAYER_POSITIONS } from 'constants/player'

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    borderBottom: '1px solid #0000001F',
  },
  dialogContent: {
    padding: '24px',
  },
  submitButton: {
    marginTop: '1rem',
  },
  helperText: {
    color: theme.palette.error.main,
  },
  errorMessage: {
    color: theme.palette.error.main,
    lineHeight: '22px',
    fontWeight: 600
  },
}))

const PlayerDialog = ({
  open,
  onClose,
  handleCreatePlayer,
  handleChange,
  formData,
  isNewPlayer,
  isLoading,
  errorMessage,
}) => {
  const classes = useStyles()

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    number: false,
  })

  const MIN_NUMBER = 1
  const MAX_NUMBER = 99

  const handleInput = (event) => {
    const { name, value } = event.target
    handleChange(event)

    if (name === 'number') {
      setErrors((prevErrors) => ({ ...prevErrors, number: false }))

      return
    }

    if (value) {
      if (name === 'firstName') {
        setErrors((prevErrors) => ({ ...prevErrors, firstName: false }))
      }
      if (name === 'lastName') {
        setErrors((prevErrors) => ({ ...prevErrors, lastName: false }))
      }
    }
  }

  const validateFields = () => {
    let isValid = true
    const newErrors = { ...errors }

    if (isNewPlayer) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = true
        isValid = false
      } else {
        newErrors.firstName = false
      }

      if (!formData.lastName.trim()) {
        newErrors.lastName = true
        isValid = false
      } else {
        newErrors.lastName = false
      }
    }

    const hasNumber = formData.number !== '' && formData.number !== null

    if (hasNumber) {
      const number = parseInt(formData.number, 10)
      if (Number.isNaN(number) || number < MIN_NUMBER || number > MAX_NUMBER) {
        newErrors.number = true
        isValid = false
      } else {
        newErrors.number = false
      }
    } else {
      newErrors.number = false
    }

    setErrors(newErrors)

    return isValid
  }

  const handleSubmit = () => {
    if (validateFields()) {
      handleCreatePlayer()
    }
  }

  useEffect(() => {
    if (open) {
      setErrors({
        firstName: false,
        lastName: false,
        number: false,
      })
    }
  }, [open, isNewPlayer])

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        {isNewPlayer ? (
          <Typography variant="h3">
            <Translate id="team.members.personal-data" />
          </Typography>
        ) : (
          <Typography variant="h3">
            <Translate id="team.members.missing-details" />
          </Typography>
        )}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box mb={3}>
          <Typography>
            {isNewPlayer ? (
              <Translate id="team.members.update-info" />
            ) : (
              <Translate id="team.members.fill-info" />
            )}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {isNewPlayer && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label={<Translate id="form.first-name" />}
                  value={formData.firstName}
                  onChange={handleInput}
                  variant="outlined"
                  fullWidth
                  required
                  name="firstName"
                  error={errors.firstName}
                  helperText={
                    errors.firstName && (
                      <Translate id="form.first-name-required" />
                    )
                  }
                  FormHelperTextProps={{ className: classes.helperText }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label={<Translate id="form.last-name" />}
                  value={formData.lastName}
                  onChange={handleInput}
                  variant="outlined"
                  fullWidth
                  required
                  name="lastName"
                  error={errors.lastName}
                  helperText={
                    errors.lastName && (
                      <Translate id="form.last-name-required" />
                    )
                  }
                  FormHelperTextProps={{ className: classes.helperText }}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <SelectedMenu
              id="position"
              name="position"
              margin="dense"
              value={formData.position}
              onChange={handleInput}
              label={<Translate id="form.position" />}
            >
              <MenuItem value="">
                <em>-</em>
              </MenuItem>
              {PLAYER_POSITIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.title}
                </MenuItem>
              ))}
            </SelectedMenu>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              type="number"
              label={<Translate id="form.number" />}
              margin="dense"
              name="number"
              value={formData.number}
              onChange={handleInput}
              error={errors.number}
              helperText={
                errors.number && <Translate id="form.number-too-large" />
              }
              FormHelperTextProps={{ className: classes.helperText }}
            />
          </Grid>
        </Grid>
        {errorMessage && (
          <Box mt={2}>
            <Typography className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          </Box>
        )}
        <Grid
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          item
          xs={12}
        >
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            className={classes.submitButton}
            disabled={isLoading}
          >
            <Translate id="player.add" />
          </Button>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

PlayerDialog.defaultProps = {
  selectedMember: null,
  errorMessage: null,
}

PlayerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedMember: PropTypes.shape({}),
  handleCreatePlayer: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    position: PropTypes.string,
    number: PropTypes.string,
  }).isRequired,
  isNewPlayer: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
}

export default PlayerDialog
